import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import QuickProductForm from './QuickProductForm';
import toast from 'react-hot-toast';

const StockReceiptModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState('search'); // 'search' | 'create' | 'allocate'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Allocation state
  const [totalQuantity, setTotalQuantity] = useState('');
  const [b2cQuantity, setB2cQuantity] = useState('');
  const [b2bQuantity, setB2bQuantity] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProducts();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const searchProducts = async () => {
    try {
      const response = await axios.get('/api/products', {
        params: { search: searchTerm, limit: 10 }
      });
      setSearchResults(response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setStep('allocate');
  };

  const handleProductCreated = (product) => {
    setSelectedProduct(product);
    setStep('allocate');
    toast.success('מוצר נוצר בהצלחה');
  };

  const handleTotalQuantityChange = (value) => {
    setTotalQuantity(value);
    // Auto-adjust B2B if B2C is set
    if (b2cQuantity) {
      setB2bQuantity(Math.max(0, parseInt(value) - parseInt(b2cQuantity)));
    }
  };

  const handleB2cQuantityChange = (value) => {
    const b2c = parseInt(value) || 0;
    const total = parseInt(totalQuantity) || 0;
    setB2cQuantity(value);
    setB2bQuantity(Math.max(0, total - b2c));
  };

  const handleB2bQuantityChange = (value) => {
    const b2b = parseInt(value) || 0;
    const total = parseInt(totalQuantity) || 0;
    setB2bQuantity(value);
    setB2cQuantity(Math.max(0, total - b2b));
  };

  const validateAllocation = () => {
    const total = parseInt(totalQuantity) || 0;
    const b2c = parseInt(b2cQuantity) || 0;
    const b2b = parseInt(b2bQuantity) || 0;
    
    if (total <= 0) {
      toast.error('יש להזין כמות גדולה מ-0');
      return false;
    }
    
    if (b2c + b2b !== total) {
      toast.error(`חלוקת המלאי אינה תואמת. סה"כ: ${total}, מחולק: ${b2c + b2b}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateAllocation()) return;

    try {
      setLoading(true);
      
      await axios.post('/api/inventory/stock-in', {
        product_id: selectedProduct.id,
        quantity: parseInt(totalQuantity),
        b2c_quantity: parseInt(b2cQuantity) || 0,
        b2b_quantity: parseInt(b2bQuantity) || 0,
        notes
      });

      toast.success('המלאי נקלט בהצלחה');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בקליטת המלאי');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {step === 'search' && 'קבלת מלאי - בחירת מוצר'}
            {step === 'create' && 'קבלת מלאי - יצירת מוצר חדש'}
            {step === 'allocate' && 'קבלת מלאי - חלוקה למחסנים'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'search' && (
            <div className="space-y-6">
              {/* Search Box */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  חיפוש מוצר קיים
                </label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="חפש לפי שם, SKU..."
                    className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y">
                  {searchResults.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {product.images && product.images[0] && (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-600">{product.sku}</div>
                          <div className="text-sm text-gray-500">
                            B2C: {product.stock_b2c || 0} | B2B: {product.stock_b2b || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Or Create New */}
              <div className="border-t pt-6">
                <button
                  onClick={() => setStep('create')}
                  className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                >
                  + יצירת מוצר חדש
                </button>
              </div>
            </div>
          )}

          {step === 'create' && (
            <div>
              <button
                onClick={() => setStep('search')}
                className="mb-4 text-blue-600 hover:text-blue-700 text-sm"
              >
                ← חזרה לחיפוש
              </button>
              <QuickProductForm onSuccess={handleProductCreated} />
            </div>
          )}

          {step === 'allocate' && selectedProduct && (
            <div className="space-y-6">
              {/* Selected Product Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-4">
                  {selectedProduct.images && selectedProduct.images[0] && (
                    <img
                      src={selectedProduct.images[0]}
                      alt={selectedProduct.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div>
                    <div className="font-medium text-gray-900">{selectedProduct.name}</div>
                    <div className="text-sm text-gray-600">{selectedProduct.sku}</div>
                    <div className="text-sm text-gray-500">
                      מלאי נוכחי - B2C: {selectedProduct.stock_b2c || 0} | B2B: {selectedProduct.stock_b2b || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  כמות כוללת שהתקבלה *
                </label>
                <input
                  type="number"
                  value={totalQuantity}
                  onChange={(e) => handleTotalQuantityChange(e.target.value)}
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              {/* Allocation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-4">חלוקה למחסנים</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🛒 למחסן B2C (קמעונאי)
                    </label>
                    <input
                      type="number"
                      value={b2cQuantity}
                      onChange={(e) => handleB2cQuantityChange(e.target.value)}
                      min="0"
                      max={totalQuantity}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🏢 למחסן B2B (סיטונאי)
                    </label>
                    <input
                      type="number"
                      value={b2bQuantity}
                      onChange={(e) => handleB2bQuantityChange(e.target.value)}
                      min="0"
                      max={totalQuantity}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Visual Representation */}
                {totalQuantity && (b2cQuantity || b2bQuantity) && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-600 mb-2">התפלגות:</div>
                    <div className="flex h-8 rounded-lg overflow-hidden border-2 border-gray-300">
                      {b2cQuantity > 0 && (
                        <div
                          className="bg-indigo-500 flex items-center justify-center text-white text-sm font-medium"
                          style={{ width: `${(parseInt(b2cQuantity) / parseInt(totalQuantity)) * 100}%` }}
                        >
                          {b2cQuantity} B2C
                        </div>
                      )}
                      {b2bQuantity > 0 && (
                        <div
                          className="bg-green-500 flex items-center justify-center text-white text-sm font-medium"
                          style={{ width: `${(parseInt(b2bQuantity) / parseInt(totalQuantity)) * 100}%` }}
                        >
                          {b2bQuantity} B2B
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Validation Message */}
                {totalQuantity && (parseInt(b2cQuantity) + parseInt(b2bQuantity) !== parseInt(totalQuantity)) && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    ⚠️ סכום החלוקה ({parseInt(b2cQuantity || 0) + parseInt(b2bQuantity || 0)}) אינו תואם לכמות הכוללת ({totalQuantity})
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  הערות (אופציונלי)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="ספק, מספר הזמנה, וכו..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setStep('search');
                    setSelectedProduct(null);
                    setTotalQuantity('');
                    setB2cQuantity('');
                    setB2bQuantity('');
                    setNotes('');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ביטול
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !totalQuantity || (parseInt(b2cQuantity) + parseInt(b2bQuantity) !== parseInt(totalQuantity))}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'מעבד...' : 'קליטת מלאי'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockReceiptModal;
